import { constants, metadata } from 'lib_ui-services';
import { errors as _errors } from 'lib_ui-primitives';
import lodash from 'lodash';
const { cloneDeep } = lodash;

const _p = {
    getLocationInfo,
    asyncConfirmationModal,
    getMinimumForeignKeyFieldsFromDictionary: metadata.getMinimumForeignKeyFieldsFromDictionary,
    getRelationMetadata: metadata.getRelationMetadata
};
export const _private = _p;

export default {
    verb: 'doingMark',
    namespace: 'item',
    relation: 'item',
    type: 'all',
    description: 'Mark event to mark all the scan records as found and save them',
    featureFlag: 'multiInventory',
    prerequisites: [
        {
            context: {
                verb: 'get',
                namespace: 'location',
                relation: 'location',
                type: 'get'
            },
            query: ({ data }) => {
                return { _id: data.locationId };
            }
        },
        {
            context: { verb: 'get', namespace: 'inventory', relation: 'inventory', type: 'find' },
            query: () => ({
                criteria: { 'inventory:status.title': 'Active' }
            })
        }
    ],
    //this is the actual logic:
    logic: markAllAsFound
};

async function markAllAsFound({ data, context, prerequisiteResults, dispatch }) {
    const locationRecords = prerequisiteResults?.[0]?.result ?? [];
    const activeInventories = prerequisiteResults?.[1]?.result ?? [];

    //filter activeInventories down to the currently selected inventory
    const activeInventory = activeInventories?.find(inv => inv._id === data.navigationSelection?.record?._id);

    // Just in case we ended up here without active inventory
    if (!activeInventory) {
        dispatch(
            {
                message: 'No active inventory found!  You must select an active inventory to mark items as found.',
                variant: 'error',
                type: constants.notificationTypes.TOAST
            },
            {
                verb: 'pop',
                namespace: 'application',
                relation: 'notification'
            }
        );
        return;
    }
    if (locationRecords.length > 1) {
        dispatch(
            {
                message: `Multiple location found for _id ${data.locationId}!`,
                variant: 'warning',
                type: constants.notificationTypes.TOAST
            },
            {
                ...context,
                verb: 'pop',
                namespace: 'application',
                relation: 'notification'
            }
        );
        return;
    }
    const { foundBy, includeUnknownTags, records } = data;

    const location = await _p.getMinimumForeignKeyFieldsFromDictionary(
        locationRecords[0],
        'item',
        'item',
        'location',
        'location'
    );
    location['location:building'] = locationRecords[0]['location:building'];
    if (includeUnknownTags) {
        const count = records.filter(
            rec =>
                rec.assetNo.startsWith(`<${constants.UNKNOWN_TAG}`) || rec.assetNo.startsWith(`<${constants.NEW_TAG}`)
        ).length;
        if (count > 0) {
            const confirm = await _p.asyncConfirmationModal(
                {
                    message: `${count} unknown tags will not be marked as found.  Continue?`
                },
                dispatch
            );
            if (!confirm) {
                throw new Error('This request was cancelled.');
            }
        }
    }
    const knownTags = records.filter(
        rec => !rec.assetNo.startsWith(`<${constants.UNKNOWN_TAG}`) && !rec.assetNo.startsWith(`<${constants.NEW_TAG}`)
    );
    const result = await Promise.all(
        knownTags.map(async read => {
            if (read.inDatabase) {
                const locationInfo = await _p.getLocationInfo(location, dispatch);
                const {
                    result: [oldRecord]
                } = await dispatch(
                    { _id: read._id },
                    {
                        verb: 'get',
                        namespace: 'item',
                        relation: 'item',
                        type: 'get'
                    },
                    true
                );
                let inventory;
                // if this asset was not part of this inventory yet
                if (
                    !oldRecord?.inventory?.find(
                        inv =>
                            inv['inventory:inventory']?._id === activeInventory._id ||
                            inv['inventory:excludedInventory']?._id === activeInventory._id
                    )
                ) {
                    // check if it was *previously* in this inventory
                    // add asset to this inventory
                    inventory = cloneDeep(oldRecord?.inventory || []);
                    inventory.push({
                        'inventory:inventory': { _id: activeInventory._id, title: activeInventory.title },
                        found: true,
                        foundDate: new Date().toISOString(),
                        foundBy,
                        foundByScan: [
                            constants.sensorTypes.BARCODE,
                            constants.sensorTypes.RFID,
                            constants.sensorTypes.BLE
                        ].includes(read.sensorType)
                    });
                } else {
                    // it's either currently in this inventory, or was previously
                    // just update the inventory information
                    inventory = oldRecord.inventory.map(inv => {
                        if (inv['inventory:inventory']?._id === activeInventory._id) {
                            // in this inventory now
                            return {
                                ...inv,
                                found: true,
                                foundDate: new Date().toISOString(),
                                foundBy,
                                foundByScan: [
                                    constants.sensorTypes.BARCODE,
                                    constants.sensorTypes.RFID,
                                    constants.sensorTypes.BLE
                                ].includes(read.sensorType)
                            };
                        } else if (inv['inventory:excludedInventory']?._id === activeInventory._id) {
                            // in this inventory previously
                            return {
                                ...inv,
                                found: true,
                                foundDate: new Date().toISOString(),
                                foundBy,
                                foundByScan: [
                                    constants.sensorTypes.BARCODE,
                                    constants.sensorTypes.RFID,
                                    constants.sensorTypes.BLE
                                ].includes(read.sensorType)
                            };
                        } else return inv;
                    });
                }
                const newRecord = {
                    ...oldRecord,
                    inventory,
                    'location:company': locationInfo.company
                        ? await _p.getMinimumForeignKeyFieldsFromDictionary(
                              locationInfo.company,
                              'item',
                              'item',
                              'location',
                              'company'
                          )
                        : undefined,
                    'location:building': locationInfo.building
                        ? await _p.getMinimumForeignKeyFieldsFromDictionary(
                              locationInfo.building,
                              'item',
                              'item',
                              'location',
                              'building'
                          )
                        : undefined,
                    'location:location': await _p.getMinimumForeignKeyFieldsFromDictionary(
                        locationInfo.location,
                        'item',
                        'item',
                        'location',
                        'location'
                    )
                };

                if (await isValid(newRecord, dispatch, context)) {
                    await dispatch(
                        { oldRecord, newRecord },
                        { verb: 'update', namespace: 'item', relation: 'item' },
                        true
                    );
                    return newRecord;
                }
            }
        })
    );
    return { ...data, result };
}

async function getLocationInfo(newLocation, dispatch) {
    let newLocationInfo = { location: newLocation };

    if (!newLocation['location:building']) return newLocationInfo;

    newLocationInfo['building'] = newLocation['location:building'];

    if (!_p.getRelationMetadata('location', 'building', false)) return newLocationInfo;

    const buildingResp = await dispatch(
        {
            criteria: { _id: newLocationInfo.building._id }
        },
        { verb: 'get', namespace: 'location', relation: 'building', type: 'find' },
        true
    );

    newLocationInfo['company'] = buildingResp?.result?.[0]?.['location:company'];
    return newLocationInfo;
}

async function isValid(newRecord, dispatch, context) {
    try {
        const { namespace, relation } = context;
        await dispatch({ newRecord }, { verb: 'validate', namespace, relation }, true);
        return true;
    } catch (err) {
        if (err instanceof _errors.ValidationError) {
            // TODO: Make asset in this string dynamic based on relation.
            let message = 'There was a problem saving this asset.';
            Object.entries(err.fieldMessages).forEach(([field, fieldMessage]) => {
                message += `\n   ${field}: ${fieldMessage}`;
            });
            dispatch(
                {
                    isError: true, //todo: gl2025 remove
                    variant: 'error',
                    message,
                    type: constants.notificationTypes.TOAST
                },
                { verb: 'pop', namespace: 'application', relation: 'notification' }
            );
        }
        // creates a _failure result.
        throw err;
    }
}

async function asyncConfirmationModal(modalInfo, dispatch) {
    return new Promise(resolve => {
        dispatch(
            {
                message: 'Are you sure you want to make this change?',
                okButtonText: 'OK',
                cancelButtonText: 'CANCEL',
                variant: constants.VARIANT.Warning,
                okAction: () => resolve(true),
                cancelAction: () => resolve(false),
                ...modalInfo
            },
            { verb: 'confirm', namespace: 'application', relation: 'user' }
        );
    });
}
